// ── PLATFORM ADAPTER (web) ────────────────────────────────────────────────────
// Web Push: Notification + navigator.serviceWorker + PushManager. Web-push is a
// browser feature; the RN/Expo port replaces this file with expo-notifications
// (APNs/FCM) but keeps the same enable/disable contract and the same POST body to
// /api/subscribe (so the D1 store + cron sender are shared, not rewritten).
import { config, hasPush } from '@/config'
import { resolveLocation, ugcFromUrl } from '@/lib/nws/client'
import type { SavedLocation } from '@/store/location'

/** The payload we POST to the subscribe endpoint (stored in D1 by the Function). */
export interface SubscribePayload {
  endpoint: string
  keys: { p256dh: string; auth: string }
  lat: number
  lon: number
  label: string
  countyUGC: string | null
  zoneUGC: string | null
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

export async function getRegistration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.ready
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.ready
  return reg.pushManager.getSubscription()
}

/**
 * Subscribe to push for a location. MUST be called from a user gesture (iOS),
 * synchronously enough that the permission prompt is tied to the tap.
 * Resolves the NWS county/zone UGC so the server can match alerts geographically.
 */
export async function enablePush(loc: SavedLocation): Promise<PushSubscription> {
  if (!hasPush) throw new Error('Push is not configured (missing VAPID public key).')

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error(permission === 'denied' ? 'Notifications are blocked in settings.' : 'Permission not granted.')
  }

  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(config.push.vapidPublicKey),
  })

  // Resolve county/zone UGC for geographic alert matching (cached by the query layer).
  let countyUGC: string | null = null
  let zoneUGC: string | null = null
  try {
    const { point } = await resolveLocation(loc.lat, loc.lon)
    countyUGC = ugcFromUrl(point.county)
    zoneUGC = ugcFromUrl(point.forecastZone)
  } catch {
    /* non-fatal — server can still push by lat/lon proximity later */
  }

  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } }
  const payload: SubscribePayload = {
    endpoint: json.endpoint,
    keys: json.keys,
    lat: loc.lat,
    lon: loc.lon,
    label: loc.label,
    countyUGC,
    zoneUGC,
  }

  const res = await fetch(`${config.push.apiBase}/subscribe`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(`subscribe failed (${res.status})`)
  return sub
}

export async function disablePush(): Promise<void> {
  const sub = await getExistingSubscription()
  if (!sub) return
  try {
    await fetch(`${config.push.apiBase}/unsubscribe`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    })
  } finally {
    await sub.unsubscribe()
  }
}
